// ./src/utils/api/CarTotalSummaryService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Car } from "./CarService";
import { Expense } from "./ExpenseService";
import { Travel } from "./TravelService";

// Fragment for TotalSummaryUnits to avoid repetition
const TOTAL_SUMMARY_UNITS_FRAGMENT = gql`
  fragment TotalSummaryUnitsFields on TotalSummaryUnits {
    distanceUnit
    volumeUnit
    latestKnownMileage
    firstRefuelOdometer
    consumptionDistance
    totalTravelsDistance
    totalRefuelsVolume
    firstRefuelVolume
    consumptionVolume
    consumptionUnit
    consumption
  }
`;

// GraphQL queries
const CAR_TOTAL_SUMMARY_LIST_QUERY = gql`
  ${TOTAL_SUMMARY_UNITS_FRAGMENT}
  query CarTotalSummaryList(
    $filter: CarTotalSummaryFilter
    $params: PaginationAndSorting
  ) {
    carTotalSummaryList(filter: $filter, params: $params) {
      code
      data {
        carId
        homeCurrency
        latestKnownMileage
        latestRefuelId
        latestExpenseId
        latestTravelId
        totalRefuelsCount
        totalExpensesCount
        refuelsTaxes
        totalRefuelsCost
        expensesFees
        expensesTaxes
        totalExpensesCost
        totalRefuelsVolume
        totalRevenuesCount
        totalRevenuesAmount
        latestRevenueId
        totalMaintenanceCount
        totalMaintenanceCost
        firstRefuelId
        firstRefuelOdometer
        firstRefuelVolume
        consumptionVolume
        consumptionDistance
        totalCheckpointsCount
        totalTravelsCount
        totalTravelsDistance
        firstRecordAt
        lastRecordAt
        updatedAt
        latestRefuel {
          id
          createdAt
          odometer
          totalPrice
        }
        latestExpense {
          id
          createdAt
          odometer
          totalPrice
        }
        latestTravel {
          id
          firstDttm
          lastDttm
          firstOdometer
          lastOdometer
        }
        latestRevenue {
          id
          createdAt
          odometer
          totalPrice
        }
        firstRefuel {
          id
          createdAt
          odometer
          totalPrice
        }
        inCarUnits {
          ...TotalSummaryUnitsFields
        }
        inUserUnits {
          ...TotalSummaryUnitsFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_TOTAL_SUMMARY_GET_QUERY = gql`
  ${TOTAL_SUMMARY_UNITS_FRAGMENT}
  query CarTotalSummaryGet($carId: ID, $homeCurrency: String) {
    carTotalSummaryGet(carId: $carId, homeCurrency: $homeCurrency) {
      code
      data {
        carId
        homeCurrency
        latestKnownMileage
        latestRefuelId
        latestExpenseId
        latestTravelId
        totalRefuelsCount
        totalExpensesCount
        refuelsTaxes
        totalRefuelsCost
        expensesFees
        expensesTaxes
        totalExpensesCost
        totalRefuelsVolume
        totalRevenuesCount
        totalRevenuesAmount
        latestRevenueId
        totalMaintenanceCount
        totalMaintenanceCost
        firstRefuelId
        firstRefuelOdometer
        firstRefuelVolume
        consumptionVolume
        consumptionDistance
        totalCheckpointsCount
        totalTravelsCount
        totalTravelsDistance
        firstRecordAt
        lastRecordAt
        updatedAt
        latestRefuel {
          id
          createdAt
          odometer
          totalPrice
        }
        latestExpense {
          id
          createdAt
          odometer
          totalPrice
        }
        latestTravel {
          id
          firstDttm
          lastDttm
          firstOdometer
          lastOdometer
        }
        latestRevenue {
          id
          createdAt
          odometer
          totalPrice
        }
        firstRefuel {
          id
          createdAt
          odometer
          totalPrice
        }
        inCarUnits {
          ...TotalSummaryUnitsFields
        }
        inUserUnits {
          ...TotalSummaryUnitsFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types

/**
 * Converted values with explicit units for distance and volume fields.
 * Used for both car-specific units and user preference units.
 */
export interface TotalSummaryUnits {
  /** Distance unit: km, mi */
  distanceUnit: string;
  /** Volume unit: l, gal-us, gal-uk */
  volumeUnit: string;
  /** Latest known mileage in specified distance unit */
  latestKnownMileage?: number | undefined;
  /** First refuel odometer reading in specified distance unit */
  firstRefuelOdometer?: number | undefined;
  /** Consumption distance in specified distance unit */
  consumptionDistance?: number | undefined;
  /** Total travels distance in specified distance unit */
  totalTravelsDistance?: number | undefined;
  /** Total refuels volume in specified volume unit */
  totalRefuelsVolume?: number | undefined;
  /** First refuel volume in specified volume unit */
  firstRefuelVolume?: number | undefined;
  /** Consumption volume in specified volume unit */
  consumptionVolume?: number | undefined;
  /** Consumption unit: l100km, km-l, mpg-us, mpg-uk, etc */
  consumptionUnit?: string | undefined
  /** Calculated fuel consumption in specified consumption unit */
  consumption?: number | undefined
}

export interface CarTotalSummary {
  carId?: string | undefined;
  homeCurrency?: string | undefined;
  /** Latest known mileage (metric: km) */
  latestKnownMileage?: number | undefined;
  latestRefuelId?: string | undefined;
  latestExpenseId?: string | undefined;
  latestTravelId?: string | undefined;
  totalRefuelsCount?: number | undefined;
  totalExpensesCount?: number | undefined;
  refuelsTaxes?: number | undefined;
  totalRefuelsCost?: number | undefined;
  expensesFees?: number | undefined;
  expensesTaxes?: number | undefined;
  totalExpensesCost?: number | undefined;
  /** Total refuels volume (metric: liters) */
  totalRefuelsVolume?: number | undefined;
  // Revenue tracking
  totalRevenuesCount?: number | undefined;
  totalRevenuesAmount?: number | undefined;
  latestRevenueId?: string | undefined;
  // Maintenance tracking (expenses where is_it_maintenance = true)
  totalMaintenanceCount?: number | undefined;
  totalMaintenanceCost?: number | undefined;
  // First refuel tracking (for consumption calculation)
  firstRefuelId?: string | undefined;
  /** First refuel odometer reading (metric: km) */
  firstRefuelOdometer?: number | undefined;
  /** First refuel volume (metric: liters) */
  firstRefuelVolume?: number | undefined;
  // Consumption metrics (excluding first refuel)
  /** Consumption volume (metric: liters) */
  consumptionVolume?: number | undefined;
  /** Consumption distance (metric: km) */
  consumptionDistance?: number | undefined;
  // Checkpoint tracking
  totalCheckpointsCount?: number | undefined;
  // Travel tracking
  totalTravelsCount?: number | undefined;
  /** Total travels distance (metric: km) */
  totalTravelsDistance?: number | undefined;
  // Timestamps
  firstRecordAt?: string | undefined;
  lastRecordAt?: string | undefined;
  updatedAt?: string | undefined;
  // Relations
  car?: Car | undefined;
  latestRefuel?: Expense | undefined;
  latestExpense?: Expense | undefined;
  latestTravel?: Travel | undefined;
  latestRevenue?: Expense | undefined;
  firstRefuel?: Expense | undefined;
  /** Values converted to car's preferred units (mileageIn, mainTankVolumeEnteredIn) */
  inCarUnits?: TotalSummaryUnits | undefined;
  /** Values converted to user's preferred units (distanceIn, volumeIn from profile) */
  inUserUnits?: TotalSummaryUnits | undefined;
}

export interface CarTotalSummaryFilter {
  carId?: string[] | null | undefined;
  homeCurrency?: string[] | null | undefined;
}

export interface CarTotalSummaryWhereInput {
  carId?: string | undefined;
  homeCurrency?: string | undefined;
}

/**
 * CarTotalSummary service that handles car total summary-related API calls.
 * This is a read-only service for viewing aggregated lifetime statistics per car and currency.
 * Supports: list, get (composite key)
 */
export class CarTotalSummaryService {
  /**
   * Get a list of car total summaries with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarTotalSummaryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_TOTAL_SUMMARY_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single car total summary by composite key (carId + homeCurrency)
   */
  static async get(carId: string, homeCurrency: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_TOTAL_SUMMARY_GET_QUERY, {
      carId,
      homeCurrency,
    });
    return result;
  }
}